import { useLayoutEffect, useRef, useState } from 'react';
import type { DependencyGraphTypes } from '@backstage/core-components';
import type { EntityNodeData } from '@backstage/plugin-catalog-graph';
import { getC4Color, getC4ElementKind } from '../lib/c4Model';
import { getDeploymentLine, getNodeTypeLine } from '../lib/stack';

const PADDING_X = 18;
const PADDING_Y = 14;
const MIN_WIDTH = 160;
const MIN_HEIGHT = 60;
const CYLINDER_RADIUS_Y = 12;
const NAME_FONT_SIZE = 14;
const TYPE_FONT_SIZE = 11;
const DESCRIPTION_FONT_SIZE = 10;
const LINE_HEIGHT = 15;
const MAX_DESCRIPTION_LENGTH = 90;
const MAX_DESCRIPTION_LINES = 2;

export function truncateDescription(description: string): string {
  const normalized = description.replace(/\s+/g, ' ').trim();
  if (normalized.length <= MAX_DESCRIPTION_LENGTH) {
    return normalized;
  }
  return `${normalized.slice(0, MAX_DESCRIPTION_LENGTH - 1).trimEnd()}…`;
}

export function wrapDescription(description: string): string[] {
  const truncated = truncateDescription(description);
  if (!truncated) {
    return [];
  }

  const perLine = Math.ceil(truncated.length / MAX_DESCRIPTION_LINES);
  const lines: string[] = [];
  let current = '';

  for (const word of truncated.split(' ')) {
    if (!current) {
      current = word;
    } else if (
      current.length + 1 + word.length <= perLine ||
      lines.length === MAX_DESCRIPTION_LINES - 1
    ) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }

  if (current) {
    lines.push(current);
  }

  return lines.slice(0, MAX_DESCRIPTION_LINES);
}

function cylinderBodyPath(width: number, height: number): string {
  const rx = width / 2;
  const ry = CYLINDER_RADIUS_Y;
  return [
    `M 0,${ry}`,
    `V ${height - ry}`,
    `A ${rx},${ry} 0 0 0 ${width},${height - ry}`,
    `V ${ry}`,
    `A ${rx},${ry} 0 0 0 0,${ry}`,
    'Z',
  ].join(' ');
}

function cylinderTopPath(width: number): string {
  const rx = width / 2;
  const ry = CYLINDER_RADIUS_Y;
  return [
    `M 0,${ry}`,
    `A ${rx},${ry} 0 0 1 ${width},${ry}`,
    `A ${rx},${ry} 0 0 1 0,${ry}`,
    'Z',
  ].join(' ');
}

export function C4Node({
  node,
}: DependencyGraphTypes.RenderNodeProps<EntityNodeData>) {
  const { entity, focused, onClick } = node;

  const contentRef = useRef<SVGGElement | null>(null);
  const [size, setSize] = useState({ width: MIN_WIDTH, height: MIN_HEIGHT });

  const isDatabase = getC4ElementKind(entity) === 'Database';
  const topExtra = isDatabase ? CYLINDER_RADIUS_Y * 2 : 0;

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element || typeof element.getBBox !== 'function') {
      return;
    }

    let box: { width: number; height: number };
    try {
      box = element.getBBox();
    } catch {
      return;
    }

    const width = Math.max(MIN_WIDTH, Math.round(box.width) + PADDING_X * 2);
    const height = Math.max(
      MIN_HEIGHT,
      Math.round(box.height) + PADDING_Y * 2 + topExtra,
    );

    if (width !== size.width || height !== size.height) {
      setSize({ width, height });
    }
  }, [size.width, size.height, topExtra]);

  const color = getC4Color(entity);
  const title = entity?.metadata?.title ?? entity?.metadata?.name ?? '';
  const typeLine = getNodeTypeLine(entity);
  const deploymentLine = getDeploymentLine(entity);
  const description = entity?.metadata?.description;
  const descriptionLines = description ? wrapDescription(description) : [];

  const strokeWidth = focused ? 4 : 2;
  const shapeProps = {
    fill: color.fill,
    stroke: color.stroke,
    strokeWidth,
  };

  const nameY = PADDING_Y + topExtra + NAME_FONT_SIZE;
  const typeY = nameY + LINE_HEIGHT + 2;
  const deploymentY = typeY + LINE_HEIGHT - 2;
  const descriptionY = (deploymentLine ? deploymentY : typeY) + LINE_HEIGHT + 2;

  return (
    <g
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      data-testid="c4-node"
    >
      {isDatabase ? (
        <>
          <path
            data-testid="c4-node-cylinder"
            d={cylinderBodyPath(size.width, size.height)}
            {...shapeProps}
          />
          <path
            data-testid="c4-node-cylinder-top"
            d={cylinderTopPath(size.width)}
            {...shapeProps}
            fill={color.stroke}
          />
        </>
      ) : (
        <rect
          data-testid="c4-node-rect"
          width={size.width}
          height={size.height}
          rx={4}
          {...shapeProps}
        />
      )}

      <g ref={contentRef} textAnchor="middle" fill={color.text}>
        <text
          data-testid="c4-node-title"
          x={size.width / 2}
          y={nameY}
          fontSize={NAME_FONT_SIZE}
          fontWeight="bold"
        >
          {title}
        </text>
        <text
          data-testid="c4-node-type"
          x={size.width / 2}
          y={typeY}
          fontSize={TYPE_FONT_SIZE}
          opacity="0.9"
        >
          {typeLine}
        </text>
        {deploymentLine && (
          <text
            data-testid="c4-node-deployment"
            x={size.width / 2}
            y={deploymentY}
            fontSize={DESCRIPTION_FONT_SIZE}
            opacity="0.85"
          >
            {deploymentLine}
          </text>
        )}
        {descriptionLines.length > 0 && (
          <text
            data-testid="c4-node-description"
            x={size.width / 2}
            y={descriptionY}
            fontSize={DESCRIPTION_FONT_SIZE}
            opacity="0.85"
          >
            {descriptionLines.map((line, index) => (
              <tspan
                key={`${index}-${line}`}
                x={size.width / 2}
                dy={index === 0 ? 0 : LINE_HEIGHT - 3}
              >
                {line}
              </tspan>
            ))}
          </text>
        )}
      </g>
    </g>
  );
}
